import type { IconName } from '../icons';
import { Icon } from './Icon';

// Mobile bottom tab bar (01 / 03): equal tabs, 24px icon over an 11px label, active = primary.
// Generic: the shell supplies the four navigation items with i18n labels; tab ids are route
// identity and never change with the App Language.
export interface TabItem<K extends string> {
  key: K;
  label: string;
  icon: IconName;
  testId?: string;
}

// Tablet / computer (≥1024, no bottom bar): the same four tabs as a row under the shell top bar
// (2026-10-20 — before this the wide layout had no way back to Home / Inspections / Reports / Settings).
export function TopTabNav<K extends string>(props: { items: TabItem<K>[]; active: K | null; onSelect: (key: K) => void; testId?: string }) {
  return (
    <nav className="topnav" data-testid={props.testId ?? 'topnav'} aria-label="primary">
      {props.items.map((item) => {
        const active = item.key === props.active;
        return (
          <button
            key={item.key}
            type="button"
            className={`topnav-item ${active ? 'topnav-item-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => props.onSelect(item.key)}
            data-testid={`topnav-${item.key}`}
            data-tab={item.key}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function BottomTabBar<K extends string>(props: { items: TabItem<K>[]; active: K | null; onSelect: (key: K) => void; testId?: string }) {
  return (
    <>
      {/* the page ground under and beside the floating pill (theme.css .tabbar-ground) */}
      <div className="tabbar-ground" aria-hidden="true" />
      <nav className="tabbar" data-testid={props.testId ?? 'tabbar'} aria-label="primary">
        {props.items.map((item) => {
          const active = item.key === props.active;
          return (
            <button
              key={item.key}
              type="button"
              className={`tabbar-item ${active ? 'tabbar-item-active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={() => props.onSelect(item.key)}
              data-testid={item.testId ?? `tabbar-${item.key}`}
              data-tab={item.key}
            >
              <Icon name={item.icon} />
              <span className="tabbar-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
